var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGames } from '~api/hooks/useGames';
import { useStructuredSelector } from '~hooks/index';
import { setActiveGame, useActiveGameSelector } from '~slices/games';
import { GAME_PERIOD } from '~slices/games/constants';
import { GAME_STATUS } from '~slices/games/games.types';
var MAX_DELAY_MS = 2147483647;
export var useUpdateActiveGameAuto = function () {
    var dispatch = useDispatch();
    var activeGame = useStructuredSelector({
        activeGame: useActiveGameSelector,
    }).activeGame;
    var getSingleGame = useGames().getSingleGame;
    var timeoutRef = useRef(null);
    var updateSingleGame = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var url, name, rounds, cupRounds, singleGame;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!activeGame)
                        return [2 /*return*/];
                    url = activeGame.url, name = activeGame.name;
                    rounds = 'rounds' in activeGame ? activeGame.rounds : undefined;
                    cupRounds = 'cupRounds' in activeGame ? activeGame.cupRounds : undefined;
                    return [4 /*yield*/, getSingleGame(url, name)];
                case 1:
                    singleGame = _a.sent();
                    if (!singleGame)
                        return [2 /*return*/];
                    dispatch(setActiveGame(__assign(__assign(__assign(__assign({}, activeGame), singleGame), (rounds && { rounds: rounds })), (cupRounds && { cupRounds: cupRounds }))));
                    return [2 /*return*/];
            }
        });
    }); }, [activeGame]);
    var checkGameStatus = useCallback(function () {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (!activeGame || Object.keys(activeGame).length === 0)
            return;
        var now = Date.now();
        var scheduleUpdate = function (delayMs) {
            if (delayMs <= 0) {
                timeoutRef.current = setInterval(updateSingleGame, 30000);
            }
            else {
                var safeDelay = Math.min(delayMs, MAX_DELAY_MS);
                timeoutRef.current = setTimeout(updateSingleGame, safeDelay);
            }
        };
        var gameStatus = activeGame.gameStatus, gameEnd = activeGame.gameEnd, gamePeriod = activeGame.gamePeriod;
        var isLastMatchStartSpecified = 'lastMatchStart' in activeGame;
        var lastMatchStart = isLastMatchStartSpecified
            ? activeGame.lastMatchStart
            : undefined;
        var isFirstMatchStartSpecified = 'firstMatchStart' in activeGame;
        var firstMatchStart = isFirstMatchStartSpecified
            ? activeGame.firstMatchStart
            : undefined;
        switch (gameStatus) {
            case GAME_STATUS.RUNNING:
                if (lastMatchStart) {
                    scheduleUpdate(lastMatchStart * 1000 - now);
                }
                else if (gameEnd) {
                    scheduleUpdate(gameEnd * 1000 - now);
                }
                break;
            case GAME_STATUS.AWAITING_START:
            case GAME_STATUS.LAST_MATCH_STARTED:
                timeoutRef.current = setInterval(updateSingleGame, 30000);
                break;
            case GAME_STATUS.AWAITING_FIRST_MATCH_START:
                if (firstMatchStart) {
                    scheduleUpdate(firstMatchStart * 1000 - now);
                }
                break;
            case GAME_STATUS.EVALUATED:
                if (gamePeriod !== GAME_PERIOD.TOURNAMENT) {
                    timeoutRef.current = setInterval(updateSingleGame, 30000);
                }
                break;
            default:
                break;
        }
    }, [activeGame]);
    useEffect(function () {
        if (!activeGame)
            return;
        checkGameStatus();
        return function () {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [activeGame]);
};
